import React, { useMemo, useState, useEffect } from 'react';
import { useIntl } from 'umi';
import classnames from 'classnames';
import { Button, Transfer, Modal } from 'tea-component';
import Loading from '@/pages/intelligence/components/Loading';
import FirstRow from '@/pages/intelligence/components/TableComponents/components/FirstRow';
import KeywordsSelect from './components/KeywordSelect';
import SourceTable from './components/SourceTable';
import TargetTable from './components/TargetTable';
import { getEntitiesKeys } from './utils';

import type { IEntity, IAddEntitiesProps } from './types';

import './index.scss';
import TipContext from '@/pages/home/components/TipContext';
import { getDetailGameOverviewPath } from '@/pages/intelligence/utils/topchartsUtils';

export default function AddEntities(porps: IAddEntitiesProps) {
  const { formatMessage } = useIntl();

  const {
    entityKey,
    title = formatMessage({ id: 'dbi_compare_add_game_to_compare' }),
    leftCellTitle = formatMessage({ id: 'dbi_compare_similar_games' }),
    rightCellTitle = formatMessage({ id: 'dbi_games' }),
    searchPlaceholder = formatMessage({ id: 'dbi_search_any_game' }),
    noSearchResultTips = formatMessage({ id: 'dbi_no_search_results' }),
    pleaseSelectTips = formatMessage({ id: 'dbi_please_a_select_game' }),
    notSelectTips = (
      <>
        {formatMessage({ id: 'dbi_compare_not_select_game_yet' })}
        <br />
        {formatMessage({ id: 'dbi_compare_please_search_game' })}
      </>
    ),

    max = 6,
    limitMsg = formatMessage({ id: 'dbi_compare_select_games_limit_tips' }, { max: max || 6 }),
    entities,
    visible,
    entityRenderer: entityRendererOutter,
    onConfirm,
    onClose,

    keywordsFetcher,
    similarEntitiesFetcher,
    entitiesFetcher,
    tipText,
  } = porps;

  function DefaultEntityRenderer(cvm: any) {
    return (
      <FirstRow
        cvm={cvm}
        rihgtClickToJumpHref={getDetailGameOverviewPath(cvm?.gameId, cvm?.entityType)}
      />
    );
  }

  const entityRenderer =
    typeof entityRendererOutter === 'function' ? entityRendererOutter : DefaultEntityRenderer;

  const entityRendererWithKeyword = (tablePayload: any) => entityRenderer(tablePayload, keyword);

  const [isLoading, setIsLoading] = useState(false);
  // 实体搜索关键字，这个关键字不是用户直接输入的，而是根据用户输入的关键字 在后台查询后 用户再次点击选择的关键字（用来搜索游戏 匹配概率更高？）
  const [keyword, setKeyword] = useState('');
  // 实体搜索关键字change
  const handleKeywordChange = async (payload: string) => {
    setKeyword(payload);
    if (!payload?.trim()) return;
    try {
      setIsLoading(true);
      let entitiesTemp = await entitiesFetcher?.(payload);
      entitiesTemp = Array.isArray(entitiesTemp) ? entitiesTemp : [];
      setSearchEntities(entitiesTemp);
    } catch (exception) {
    } finally {
      setIsLoading(false);
    }
  };

  // similar实体数据
  const [similarEntities, setSimilarEntities] = useState<IEntity[]>([]);
  // 搜索出来的实体数据
  const [searchEntities, setSearchEntities] = useState<IEntity[]>([]);
  // 勾选的实体数据
  const [checkedEntities, setCheckedEntities] = useState<IEntity[]>([]);
  // 勾选的实体数据的keys
  const checkedEntitiesKeys = useMemo<string[]>(
    () => getEntitiesKeys(checkedEntities, entityKey),
    [entityKey, checkedEntities],
  );
  const handleCheckedEntitiesKeysChange = (payload: string[]) => {
    // console.log('handleCheckedEntitiesKeysChange', payload);
    const checkedEntitiesTemp: IEntity[] = [];
    const similarEntitiesKeysMap: {
      [key: string]: boolean;
    } = {};
    const similarEntitiesAndSearchEntitiesTemp = [
      ...checkedEntities,
      ...similarEntities,
      ...searchEntities,
    ];
    for (const entity of similarEntitiesAndSearchEntitiesTemp) {
      const entityKeyTemp = entity?.[entityKey];
      if (!payload?.includes(entityKeyTemp)) continue;
      if (similarEntitiesKeysMap[entityKeyTemp]) continue;
      checkedEntitiesTemp.push(entity);
      similarEntitiesKeysMap[entityKeyTemp] = true;
    }
    setCheckedEntities(checkedEntitiesTemp?.slice(0, max));
  };
  const isDisabled = useMemo<boolean>(
    () => checkedEntitiesKeys?.length >= max,
    [checkedEntitiesKeys?.length, max],
  );

  const handleRemoveCheckedEntity = (payload: string) => {
    const checkedEntitiesTemp: IEntity[] =
      checkedEntities?.filter((item) => item?.[entityKey] !== payload) || [];
    setCheckedEntities(checkedEntitiesTemp);
  };

  const handleClearAll = () => {
    setCheckedEntities([]);
  };

  const handleConfirm = (e?: React.MouseEvent<Element, MouseEvent>) => {
    e?.stopPropagation();
    e?.preventDefault();
    onConfirm?.(checkedEntitiesKeys, checkedEntities);
  };

  const handleCancel = (e?: React.MouseEvent<Element, MouseEvent>) => {
    e?.stopPropagation();
    e?.preventDefault();
    onClose?.();
  };

  const initData = async () => {
    if (!Array.isArray(entities)) return;
    setIsLoading(true);
    setCheckedEntities(entities);
    try {
      let similarEntitiesTemp = await similarEntitiesFetcher?.(entities);
      similarEntitiesTemp = Array.isArray(similarEntitiesTemp) ? similarEntitiesTemp : [];
      setSimilarEntities(similarEntitiesTemp);
    } catch (exception) {
      setSimilarEntities([]);
    } finally {
      setIsLoading(false);
    }
  };

  const resetData = () => {
    setKeyword('');
    setSearchEntities([]);
    setSimilarEntities([]);
    setCheckedEntities([]);
    setIsLoading(false);
  };

  useEffect(() => {
    if (visible) {
      initData();
    } else {
      resetData();
    }
  }, [visible]);

  const renderSearchedGames = () => {
    if (!searchEntities?.length)
      return <div className="dbi-table-no-data dbi-left-cell">{noSearchResultTips}</div>;
    return (
      <SourceTable
        title={leftCellTitle}
        isDisabled={isDisabled}
        disabledMsg={limitMsg}
        hideHeader={true}
        entityKey={entityKey}
        records={searchEntities}
        value={checkedEntitiesKeys}
        onChange={handleCheckedEntitiesKeysChange}
        entityRenderer={entityRendererWithKeyword}
      />
    );
  };

  const renderSimilarGames = () => {
    if (!similarEntities?.length)
      return <div className="dbi-table-no-data dbi-left-cell">{notSelectTips}</div>;
    return (
      <SourceTable
        title={leftCellTitle}
        isDisabled={isDisabled}
        disabledMsg={limitMsg}
        entityKey={entityKey}
        records={similarEntities}
        value={checkedEntitiesKeys}
        onChange={handleCheckedEntitiesKeysChange}
        entityRenderer={entityRenderer}
      />
    );
  };

  const renderLeftCell = () => {
    if (isLoading) return <Loading />;
    if (keyword) return renderSearchedGames();
    return renderSimilarGames();
  };

  const renderRightCell = () => (
    <TargetTable
      className={classnames({ 'is-no-data': !checkedEntities?.length })}
      title={rightCellTitle}
      pleaseSelectTips={pleaseSelectTips}
      entityKey={entityKey}
      records={checkedEntities}
      onRemove={handleRemoveCheckedEntity}
      entityRenderer={entityRenderer}
    />
  );
  return (
    <Modal
      className="dbi-add-entities-modal"
      visible={visible}
      caption={
        <div className="dbi-add-title">
          {title}
          {tipText && <TipContext className="dbi-add-title-icon" tipText={tipText} />}
        </div>
      }
      size={800}
      onClose={() => onClose?.()}
    >
      <Modal.Body>
        <Transfer
          // selection-centered 组件库使用的 公共样式
          className="dbi-add-games-compare selection-centered"
          leftCell={
            <Transfer.Cell
              scrollable={false}
              title={formatMessage({ id: 'dbi_add' })}
              header={
                <KeywordsSelect
                  searchPlaceholder={searchPlaceholder}
                  keywordsFetcher={keywordsFetcher}
                  value={keyword}
                  onChange={handleKeywordChange}
                />
              }
            >
              {renderLeftCell()}
            </Transfer.Cell>
          }
          rightCell={
            <Transfer.Cell
              className="dbi-compare-right"
              scrollable={false}
              title={
                <div className="compare-title-flex">
                  <div className="compare-title">
                    {formatMessage({ id: 'dbi_compare_selected' })} ({checkedEntitiesKeys?.length})
                  </div>
                  {checkedEntities?.length > 0 && (
                    <Button type="link" className="compare-btn" onClick={handleClearAll}>
                      {formatMessage({ id: 'dbi_compare_clear_all' })}
                    </Button>
                  )}
                </div>
              }
            >
              {renderRightCell()}
            </Transfer.Cell>
          }
        />
      </Modal.Body>
      <Modal.Footer>
        <Button type="primary" onClick={handleConfirm}>
          {formatMessage({ id: 'dbi_confirm' })}
        </Button>
        <Button type="weak" onClick={handleCancel}>
          {formatMessage({ id: 'dbi_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export type { IAddEntitiesProps };
